import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { NegotiationsUtils } from '@services/negotiations-utils';
import TagContainer from '@enums/tagContainer';
import { useAnalytics } from '@context/analytics/analytics.context';
import { useNegotiation } from '@context/negotiation.context';
import NegotiationForm from '@components/organisms/negotiation/negotiation-modal/form/negotiation-form';
import { usePreferences } from '@context/preferences.context';
import { NegotiationModalProductInfo } from '@components/organisms/negotiation/negotiation-modal/product-info/negotiation-modal-product';
import { ImagesService } from '@services/image-service';
import { NegotiationEvent } from '@interfaces/models/negotiation/negotiationEvent';
import { useNegotiationModalPresenter } from '../negotiation-modal-presenter';
import styles from '../negotiation-modal.module.scss';
import { NegotiationModalOngoingOffers } from '../ongoing-offers/negotiation-modal-ongoing-offers';
import { NegotiationModalPricingInsight } from '../pricing-insight/negotiation-modal-pricing-insight';

type NegotiationModalNewNegotiationProps = {
  trackingFrom: string;
  buyerId: string;
  isShippingProhibited: boolean;
  productPath: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
};

export const NegotiationModalNew: FC<NegotiationModalNewNegotiationProps> = ({
  trackingFrom,
  buyerId,
  isShippingProhibited,
  productPath,
  setIsLoading,
  onClose,
}) => {
  const {
    negotiation,
    negotiationProduct,
    isNegotiationBannerVisible,
    isBuyNowCTAVisible,
    concurrentActiveNegotiations,
  } = useNegotiationModalPresenter();
  const { sendEvent } = useAnalytics();
  const { initialData, setNegotiation } = useNegotiation();
  const { currency, language } = usePreferences();
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const userRole = initialData?.callerContext;
  const negotiationCurrency =
    userRole === NegotiationEvent.CreatedByEnum.Seller ? initialData.product.price.currency : currency;
  const minOffer = Math.round(initialData.offerConstraints.minimumAmount.cents / 100);
  const maxOffer = Math.round(initialData.offerConstraints.maximumAmount.cents / 100);

  const submitOffer = async (price: number) => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const negotiation: Negotiation = await NegotiationsUtils.createNegotiation(
        String(initialData.product.id),
        price,
        buyerId,
        initialData.product.price.currency,
      );
      setIsLoading(false);
      setNegotiation(negotiation);
      // TODO: [negotiationEvent] Check if this is the correct event to send
      sendEvent(
        {
          type: 'event',
          container: TagContainer.Analytics,
          payload: {
            event: 'productMmao',
            env_template: 'product',
            product_brand: initialData.tracking.productBrandName,
            product_univers: initialData.tracking.productUniverseName,
            product_category: initialData.tracking.productCategoryName,
          },
        },
        {
          type: 'MakeMeOffer',
          container: TagContainer.Media,
        },
      );
    } catch (e) {
      setIsLoading(false);
      if (e) {
        setErrorMessage(e);
      }
    }
  };

  const shouldShowWarning = (): boolean => {
    return userRole === NegotiationEvent.CreatedByEnum.Seller && initialData?.product?.displayPricingInsight;
  };

  return (
    <>
      <div className={styles.mmao__innerWrapper}>
        {errorMessage && <p className={styles.mmao__error}>{errorMessage}</p>}
        <NegotiationModalProductInfo
          price={initialData.product.price.formatted}
          isSeller={userRole === NegotiationEvent.CreatedByEnum.Seller}
          brandName={initialData.product.headline}
          breakdown={negotiationProduct.pricingBreakdown}
          description={initialData.product.subHeadline}
          picturePath={ImagesService.buildUrl(`/produit/${initialData.product.picturePath}`, { w: 244 }, true)}
          productPath={productPath}
          alt={initialData?.product?.headline}
        />
        <NegotiationModalPricingInsight shouldShow={shouldShowWarning()} />
        {isNegotiationBannerVisible && (
          <NegotiationModalOngoingOffers
            trackingFrom={trackingFrom}
            showBuyNow={isBuyNowCTAVisible}
            setIsLoading={setIsLoading}
            onClose={onClose}
            isNegotiationAccepted={negotiation?.isAccepted}
            product={negotiationProduct}
            concurrentActiveNegotiations={concurrentActiveNegotiations}
          />
        )}
        <div className={styles.mmao__step}>
          <div
            className={styles.mmao__action}
            data-cy="mmao_action"
          >
            <NegotiationForm
              isDisabled={isShippingProhibited}
              action="new"
              currency={negotiationCurrency}
              language={language}
              min={minOffer}
              max={maxOffer}
              onSubmit={submitOffer}
              productId={`${initialData.product.id}`}
              isSeller={userRole === NegotiationEvent.CreatedByEnum.Seller}
            />
          </div>
        </div>
      </div>
    </>
  );
};
